import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Files as FilesIcon,
  Upload,
  Download,
  Trash2,
  FileText,
  Image as ImageIcon,
  Film,
  File as FileGeneric,
  Loader2,
  Globe,
  Lock,
  Link as LinkIcon,
  X,
  GripVertical,
  ArrowUpDown,
  Check,
  Search,
  Folder,
  FolderPlus,
  ChevronLeft,
  MoveRight,
  Pencil,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { filesApi, foldersApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import FilePreviewModal from "@/components/FilePreviewModal";
import DocumentViewer, { isViewableDocument } from "@/components/DocumentViewer";

const MAX_BYTES = 250 * 1024 * 1024;

const SORT_OPTIONS = [
  { value: "custom", label: "Custom order", hint: "Drag to reorder" },
  { value: "name_asc", label: "Name (A → Z)" },
  { value: "name_desc", label: "Name (Z → A)" },
  { value: "date_desc", label: "Newest first" },
  { value: "date_asc", label: "Oldest first" },
  { value: "size_desc", label: "Largest first" },
  { value: "size_asc", label: "Smallest first" },
];

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function iconFor(mime, name) {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("image/")) return ImageIcon;
  if (m.startsWith("video/")) return Film;
  if (m.includes("pdf") || (name || "").toLowerCase().endsWith(".pdf")) return FileText;
  return FileGeneric;
}

export default function FilesPanel({ projectId, myRole }) {
  const { user: me } = useAuth();
  const [files, setFiles] = useState([]);
  // Folders (Feb 2026 → nested Feb 2026): flat list from server; we build the
  // tree in-memory. `parent_folder_id === null` marks a root-level folder.
  // We cap nesting at 3 levels (root → L1 → L2 → L3); enforcement is
  // primarily server-side, the UI just disables "New folder" at max depth.
  const MAX_FOLDER_DEPTH = 2; // 0-indexed, matches backend
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = root
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renamingFolderName, setRenamingFolderName] = useState("");
  // Native HTML5 DnD state — tracks what item is being dragged so the
  // folder tiles / breadcrumbs can highlight themselves as drop targets
  // without blocking @hello-pangea/dnd used for file reordering.
  const [dragItem, setDragItem] = useState(null); // {type: 'file'|'folder', id}
  const [dragOverFolderId, setDragOverFolderId] = useState(null); // null = root drop
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewFile, setPreviewFile] = useState(null);
  // Office documents (docx/xlsx/xls/csv) route to the DocumentViewer
  // instead of the generic FilePreviewModal so users get an in-browser
  // Word-like / Excel-like read of the file rather than an "unsupported"
  // fallback. Kept in a separate state slot so both modals can never be
  // open simultaneously.
  const [docViewerFile, setDocViewerFile] = useState(null);

  const openPreview = (f) => {
    if (isViewableDocument(f)) setDocViewerFile(f);
    else setPreviewFile(f);
  };
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sortBy, setSortBy] = useState("custom");
  const [filterQuery, setFilterQuery] = useState("");
  const inputRef = useRef(null);

  // Effective role on this project (project membership wins, falls back to global role).
  const effectiveRole = myRole || me?.role || "viewer";
  const isAdmin = me?.role === "admin" || effectiveRole === "admin";
  // Contributors get file upload/download rights identical to editors — even
  // though they can't edit tasks or plans elsewhere in the app.
  const hasFileEditAccess =
    isAdmin ||
    effectiveRole === "editor" ||
    effectiveRole === "manager" ||
    effectiveRole === "contributor";
  const isEditorOrAbove = hasFileEditAccess;
  const canUpload = isEditorOrAbove;
  const canDownload = isEditorOrAbove; // Viewers can preview but not save/zip
  // Editors/contributors/admins can drag-reorder, but ONLY when viewing in custom order with no filter active
  const canReorder = isEditorOrAbove && sortBy === "custom" && !filterQuery.trim();
  const inSelectionMode = selected.size > 0;

  // Filter + sort the files for display. Filter runs first so sort applies to visible rows.
  const displayedFiles = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    let base = files;
    if (q) {
      base = files.filter((f) =>
        ((f.name || "") + " " + (f.mime || "")).toLowerCase().includes(q)
      );
    }
    if (sortBy === "custom") return base;
    const copy = [...base];
    const cmp = {
      name_asc: (a, b) => a.name.localeCompare(b.name),
      name_desc: (a, b) => b.name.localeCompare(a.name),
      date_asc: (a, b) => (a.uploaded_at || "").localeCompare(b.uploaded_at || ""),
      date_desc: (a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""),
      size_asc: (a, b) => (a.size || 0) - (b.size || 0),
      size_desc: (a, b) => (b.size || 0) - (a.size || 0),
    }[sortBy];
    return cmp ? copy.sort(cmp) : copy;
  }, [files, sortBy, filterQuery]);

  const currentSortLabel =
    SORT_OPTIONS.find((o) => o.value === sortBy)?.label || "Custom order";

  const load = async () => {
    setLoading(true);
    try {
      const [filesData, foldersData] = await Promise.all([
        filesApi.list(projectId, currentFolderId ?? "root"),
        foldersApi.list(projectId),
      ]);
      setFiles(filesData);
      setFolders(foldersData);
    } catch {
      toast.error("Failed to load files");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) load();
     
  }, [projectId, currentFolderId]);

  // Clear selection when the project changes
  useEffect(() => {
    setSelected(new Set());
  }, [projectId]);

  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    // Validate size + tally what we'll actually upload
    const validFiles = [];
    for (const file of files) {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} is too large (${formatBytes(file.size)}). The upload limit is 250 MB.`);
        continue;
      }
      validFiles.push(file);
    }
    if (!validFiles.length) return;

    setUploading(true);
    setProgress(0);
    let succeeded = 0;
    let failed = 0;
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      try {
        // Progress reflects overall batch position + this file's byte progress
        await filesApi.upload(
          projectId,
          file,
          (e) => {
            if (e?.total) {
              const fileFraction = e.loaded / e.total;
              const batchFraction = (i + fileFraction) / validFiles.length;
              setProgress(Math.round(batchFraction * 100));
            }
          },
          currentFolderId, // upload into the folder we're currently viewing
        );
        succeeded += 1;
      } catch (err) {
        failed += 1;
        toast.error(err?.response?.data?.detail || `${file.name}: upload failed`);
      }
    }
    if (succeeded > 0) {
      toast.success(
        succeeded === 1
          ? `${validFiles[0].name} uploaded`
          : `${succeeded} files uploaded${failed ? ` (${failed} failed)` : ""}`,
      );
    }
    load();
    setUploading(false);
    setProgress(0);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleRemove = async (file) => {
    if (!window.confirm(`Remove "${file.name}"? This can't be undone.`)) return;
    try {
      await filesApi.remove(projectId, file.id);
      toast.success(`${file.name} removed`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  const canDeleteFile = (f) => {
    if (!me) return false;
    // Contributors can upload but NOT delete — even their own uploads.
    return (
      isAdmin ||
      ((effectiveRole === "editor" || effectiveRole === "manager") &&
        f.uploaded_by_id === me.id)
    );
  };

  // Folder actions require editor+ (contributors can upload but not organize)
  const canManageFolders =
    isAdmin || effectiveRole === "editor" || effectiveRole === "manager";

  const currentFolder = folders.find((f) => f.id === currentFolderId) || null;

  // Build helpers off the flat folder list. All O(n) — cheap even with
  // hundreds of folders per project.
  const folderById = useMemo(() => {
    const m = new Map();
    folders.forEach((f) => m.set(f.id, f));
    return m;
  }, [folders]);

  const childrenByParent = useMemo(() => {
    const m = new Map();
    folders.forEach((f) => {
      const pid = f.parent_folder_id || null;
      if (!m.has(pid)) m.set(pid, []);
      m.get(pid).push(f);
    });
    // Alphabetical inside each parent
    m.forEach((arr) =>
      arr.sort((a, b) =>
        (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()),
      ),
    );
    return m;
  }, [folders]);

  // Walk parents to build the breadcrumb trail (root → current).
  const folderPath = useMemo(() => {
    if (!currentFolderId) return [];
    const trail = [];
    let cursor = folderById.get(currentFolderId);
    const seen = new Set();
    while (cursor && !seen.has(cursor.id)) {
      seen.add(cursor.id);
      trail.unshift(cursor);
      cursor = cursor.parent_folder_id
        ? folderById.get(cursor.parent_folder_id)
        : null;
    }
    return trail;
  }, [currentFolderId, folderById]);

  // Depth of the current folder (root = -1, top-level folder = 0, ...).
  const currentDepth = folderPath.length - 1;

  // Folders visible right now: children of currentFolderId (or root children
  // when we're at root).
  const visibleFolders = childrenByParent.get(currentFolderId || null) || [];

  // Descendants of a folder (used to filter Move-To options — you can't move
  // a folder into itself or one of its descendants).
  const descendantsOf = (folderId) => {
    const out = new Set([folderId]);
    const stack = [folderId];
    while (stack.length) {
      const id = stack.pop();
      const kids = childrenByParent.get(id) || [];
      kids.forEach((k) => {
        if (!out.has(k.id)) {
          out.add(k.id);
          stack.push(k.id);
        }
      });
    }
    return out;
  };

  // Flatten the folder tree for "Move to…" menus. Returns
  // [{folder, depth}] in DFS order — useful for indented lists.
  const flattenFolders = (excludeIds = new Set()) => {
    const out = [];
    const walk = (parentId, depth) => {
      const kids = childrenByParent.get(parentId) || [];
      for (const k of kids) {
        if (excludeIds.has(k.id)) continue;
        out.push({ folder: k, depth });
        walk(k.id, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const created = await foldersApi.create(projectId, name, currentFolderId);
      setFolders((prev) => [...prev, created]);
      toast.success(`Folder "${name}" created`);
      setNewFolderOpen(false);
      setNewFolderName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create folder");
    }
  };

  const handleRenameFolder = async (folderId) => {
    const name = renamingFolderName.trim();
    if (!name) return;
    try {
      await foldersApi.rename(projectId, folderId, name);
      setFolders((prev) =>
        prev
          .map((f) => (f.id === folderId ? { ...f, name } : f))
          .sort((a, b) =>
            (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()),
          ),
      );
      toast.success("Renamed");
      setRenamingFolderId(null);
      setRenamingFolderName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    }
  };

  const handleDeleteFolder = async (folderId) => {
    const folder = folders.find((f) => f.id === folderId);
    if (!folder) return;
    if (
      !window.confirm(
        `Delete "${folder.name}"? Files inside will move back to root — none will be deleted.`,
      )
    ) {
      return;
    }
    try {
      await foldersApi.remove(projectId, folderId);
      setFolders((prev) => prev.filter((f) => f.id !== folderId));
      if (currentFolderId === folderId) setCurrentFolderId(null);
      // Reload so files that moved out show up correctly
      load();
      toast.success(`Folder "${folder.name}" removed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const handleMoveFile = async (fileId, folderId) => {
    try {
      await filesApi.move(projectId, fileId, folderId);
      // Remove from current view (it's now in a different folder)
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      const destName = folderId
        ? folderById.get(folderId)?.name || "folder"
        : null;
      toast.success(destName ? `Moved to "${destName}"` : "Moved back to root");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed");
    }
  };

  const handleMoveFolder = async (folderId, newParentId) => {
    try {
      await foldersApi.move(projectId, folderId, newParentId);
      setFolders((prev) =>
        prev.map((f) =>
          f.id === folderId ? { ...f, parent_folder_id: newParentId } : f,
        ),
      );
      const destName = newParentId
        ? folderById.get(newParentId)?.name || "folder"
        : null;
      toast.success(destName ? `Moved into "${destName}"` : "Moved to root");
      // If we're currently viewing the moved folder or one of its parents,
      // keep the view stable (it's the same folder id). No reload needed.
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Folder move failed");
    }
  };

  // Bulk actions only make sense when the user can either download OR delete something
  const canBulkAction = canDownload || isAdmin;

  // Bulk-delete is allowed if user can delete EVERY selected file
  const canBulkDelete = useMemo(() => {
    if (!inSelectionMode) return false;
    const selectedFiles = files.filter((f) => selected.has(f.id));
    return selectedFiles.every((f) => canDeleteFile(f));
     
  }, [files, selected, inSelectionMode, me]);

  const handleTogglePublic = async (f) => {
    try {
      const next = !f.is_public;
      await filesApi.setPublic(projectId, f.id, next);
      toast.success(next ? "Public link enabled" : "Made private");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const handleCopyPublicLink = async (f) => {
    try {
      await navigator.clipboard.writeText(f.download_url);
      toast.success("Public link copied");
    } catch {
      window.prompt("Copy this link:", f.download_url);
    }
  };

  /* ---------- Selection helpers ---------- */
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(displayedFiles.map((f) => f.id)));
  const clearSelection = () => setSelected(new Set());
  const allSelected = displayedFiles.length > 0 && selected.size === displayedFiles.length;

  /* ---------- Bulk actions ---------- */
  const handleBulkDownload = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await filesApi.bulkDownloadZip(projectId, ids);
      // Pull filename out of the Content-Disposition header (fallback to a default)
      const disp = res.headers?.["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      const filename = (match && match[1]) || `files_${new Date().toISOString().slice(0, 10)}.zip`;
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ids.length} ${ids.length === 1 ? "file" : "files"} as ZIP`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "ZIP download failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await filesApi.bulkDelete(projectId, ids);
      toast.success(
        `Deleted ${res.deleted}${res.skipped ? ` · ${res.skipped} skipped (not owner)` : ""}`
      );
      clearSelection();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  /* ---------- Reorder ---------- */
  const handleDragEnd = async (result) => {
    if (sortBy !== "custom") return; // Defensive — should be unreachable since drag is disabled.
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const next = Array.from(files);
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setFiles(next); // optimistic
    try {
      await filesApi.reorder(projectId, next.map((f) => f.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save new order");
      load(); // restore from server
    }
  };

  return (
    <section
      data-testid="project-files-panel"
      className="rounded-md border border-zinc-800 bg-zinc-900/60 p-6"
    >
      {/* Header / Bulk-action bar */}
      {inSelectionMode ? (
        <div
          data-testid="bulk-action-bar"
          className="flex items-center justify-between gap-3 mb-4 flex-wrap p-2 -m-2 rounded-sm bg-amber-500/10 border border-amber-500/30"
        >
          <div className="flex items-center gap-3">
            <span
              data-testid="bulk-selected-count"
              className="text-sm font-semibold text-amber-400"
            >
              {selected.size} selected
            </span>
            {!allSelected && displayedFiles.length > selected.size && (
              <button
                data-testid="bulk-select-all"
                onClick={selectAll}
                className="text-xs text-zinc-400 hover:text-zinc-100 underline"
              >
                Select all ({displayedFiles.length})
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canDownload && (
              <button
                data-testid="bulk-download-btn"
                onClick={handleBulkDownload}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-500 text-black hover:bg-amber-400 rounded-sm disabled:opacity-60"
              >
                {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                Download {selected.size > 1 ? "ZIP" : ""}
              </button>
            )}
            {canBulkDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    data-testid="bulk-delete-btn"
                    disabled={bulkBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-transparent border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 rounded-sm disabled:opacity-60"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Delete {selected.size} {selected.size === 1 ? "file" : "files"}?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-500">
                      This will permanently remove the selected files from this project.
                      This action can&apos;t be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="bulk-delete-confirm"
                      onClick={handleBulkDelete}
                      className="bg-rose-500 text-zinc-50 hover:bg-rose-600"
                    >
                      Delete {selected.size}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <button
              data-testid="bulk-clear-btn"
              onClick={clearSelection}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 rounded-sm"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            {files.length > 0 && canBulkAction && (
              <input
                type="checkbox"
                data-testid="select-all-checkbox"
                checked={allSelected}
                onChange={(e) => (e.target.checked ? selectAll() : clearSelection())}
                className="w-3.5 h-3.5 accent-amber-500 cursor-pointer"
                title="Select all"
              />
            )}
            <FilesIcon className="w-4 h-4 text-amber-500" />
            <div className="text-overline text-zinc-400">Files</div>
            <span className="text-xs text-zinc-500 font-mono">
              · {filterQuery.trim() ? `${displayedFiles.length} of ${files.length}` : files.length}
            </span>

            {files.length > 0 && (
              <div className="relative ml-3 hidden sm:block">
                <Search className="w-3 h-3 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  data-testid="files-filter-input"
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  placeholder="Filter by name or .ext…"
                  className="bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-100 pl-7 pr-7 py-1 w-48 focus:outline-none focus:border-amber-500/40 focus:ring-1 focus:ring-amber-500/20"
                />
                {filterQuery && (
                  <button
                    type="button"
                    onClick={() => setFilterQuery("")}
                    data-testid="files-filter-clear"
                    title="Clear filter"
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-zinc-500 hover:text-zinc-100"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}

            {files.length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="sort-by-trigger"
                    className="ml-3 inline-flex items-center gap-1.5 px-2 py-1 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 rounded-sm border border-zinc-800"
                    title="Change sort order"
                  >
                    <ArrowUpDown className="w-3 h-3" />
                    <span className="hidden sm:inline">{currentSortLabel}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 min-w-[200px]"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <DropdownMenuItem
                      key={opt.value}
                      data-testid={`sort-${opt.value}`}
                      onClick={() => setSortBy(opt.value)}
                      className="flex items-center justify-between cursor-pointer focus:bg-zinc-900 focus:text-zinc-100"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm">{opt.label}</span>
                        {opt.hint && (
                          <span className="text-[10px] text-zinc-500">{opt.hint}</span>
                        )}
                      </div>
                      {sortBy === opt.value && (
                        <Check className="w-3.5 h-3.5 text-amber-500 ml-3 shrink-0" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {canUpload && (
            <>
              <input
                ref={inputRef}
                type="file"
                multiple
                onChange={(e) => handleUpload(e.target.files)}
                className="hidden"
                data-testid="file-input"
              />
              <button
                data-testid="upload-file-btn"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold bg-amber-500 text-black hover:bg-amber-400 rounded-sm disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Uploading {progress}%
                  </>
                ) : (
                  <>
                    <Upload className="w-3 h-3" /> Upload
                  </>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
        </div>
      ) : (
        <div
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            if (canUpload) handleUpload(e.dataTransfer.files);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (canUpload && !dragActive) setDragActive(true);
          }}
          onDragLeave={(e) => {
            // Only clear when the drag actually leaves the outer container
            if (e.currentTarget === e.target) setDragActive(false);
          }}
          className={`relative rounded-sm transition-colors ${
            dragActive
              ? "ring-2 ring-amber-500/60 bg-amber-500/5"
              : ""
          }`}
        >
          {/* Folder breadcrumb + folder tiles */}
          <div className="mb-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div
                data-testid="folder-breadcrumbs"
                className="flex items-center gap-1 text-xs font-mono uppercase tracking-wide text-zinc-500 flex-wrap"
              >
                <BreadcrumbCrumb
                  label="All files"
                  isCurrent={!currentFolder}
                  onClick={() => setCurrentFolderId(null)}
                  isDropTarget={
                    dragItem && dragOverFolderId === "__root__" && currentFolder
                  }
                  onDragOver={(e) => {
                    if (!dragItem) return;
                    e.preventDefault();
                    setDragOverFolderId("__root__");
                  }}
                  onDragLeave={() => setDragOverFolderId(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (!dragItem) return;
                    if (dragItem.type === "file") {
                      handleMoveFile(dragItem.id, null);
                    } else if (dragItem.type === "folder") {
                      handleMoveFolder(dragItem.id, null);
                    }
                    setDragItem(null);
                    setDragOverFolderId(null);
                  }}
                  testid="folder-breadcrumb-root"
                />
                {folderPath.map((f, i) => {
                  const isLeaf = i === folderPath.length - 1;
                  return (
                    <span
                      key={f.id}
                      className="flex items-center gap-1"
                    >
                      <span className="text-zinc-700">/</span>
                      <BreadcrumbCrumb
                        label={f.name}
                        isCurrent={isLeaf}
                        onClick={() => !isLeaf && setCurrentFolderId(f.id)}
                        isDropTarget={
                          dragItem && dragOverFolderId === f.id && !isLeaf
                        }
                        onDragOver={(e) => {
                          if (!dragItem || isLeaf) return;
                          e.preventDefault();
                          setDragOverFolderId(f.id);
                        }}
                        onDragLeave={() => setDragOverFolderId(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (!dragItem || isLeaf) return;
                          if (dragItem.type === "file") {
                            handleMoveFile(dragItem.id, f.id);
                          } else if (dragItem.type === "folder") {
                            handleMoveFolder(dragItem.id, f.id);
                          }
                          setDragItem(null);
                          setDragOverFolderId(null);
                        }}
                        testid={`folder-breadcrumb-${f.id}`}
                      />
                    </span>
                  );
                })}
              </div>
              {canManageFolders && currentDepth < MAX_FOLDER_DEPTH && (
                <button
                  data-testid="new-folder-btn"
                  onClick={() => {
                    setNewFolderName("");
                    setNewFolderOpen(true);
                  }}
                  className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-amber-400 px-2 py-1 border border-zinc-800 hover:border-amber-500/40 rounded-sm"
                  title={
                    currentFolder
                      ? `New folder inside "${currentFolder.name}"`
                      : "New folder"
                  }
                >
                  <FolderPlus className="w-3.5 h-3.5" /> New folder
                </button>
              )}
            </div>

            {visibleFolders.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {visibleFolders.map((f) => {
                  // Descendants of the folder being dragged (if any) so we
                  // can grey them out as illegal drop targets.
                  const isDragSource =
                    dragItem?.type === "folder" && dragItem.id === f.id;
                  const isDescendantOfDrag =
                    dragItem?.type === "folder" &&
                    descendantsOf(dragItem.id).has(f.id);
                  const dropDisabled = isDragSource || isDescendantOfDrag;
                  return (
                    <FolderTile
                      key={f.id}
                      folder={f}
                      onOpen={() => setCurrentFolderId(f.id)}
                      onStartRename={() => {
                        setRenamingFolderId(f.id);
                        setRenamingFolderName(f.name);
                      }}
                      onDelete={() => handleDeleteFolder(f.id)}
                      canManage={canManageFolders}
                      isRenaming={renamingFolderId === f.id}
                      renamingName={renamingFolderName}
                      onRenamingChange={setRenamingFolderName}
                      onRenameSubmit={() => handleRenameFolder(f.id)}
                      onRenameCancel={() => {
                        setRenamingFolderId(null);
                        setRenamingFolderName("");
                      }}
                      // Nested-folder DnD + move
                      moveOptions={flattenFolders(descendantsOf(f.id))}
                      currentParentId={f.parent_folder_id || null}
                      onMoveTo={(destId) => handleMoveFolder(f.id, destId)}
                      isDropTarget={
                        dragItem && dragOverFolderId === f.id && !dropDisabled
                      }
                      dropDisabled={dropDisabled}
                      onDragStart={() => setDragItem({ type: "folder", id: f.id })}
                      onDragEnd={() => {
                        setDragItem(null);
                        setDragOverFolderId(null);
                      }}
                      onDragOver={(e) => {
                        if (!dragItem || dropDisabled) return;
                        e.preventDefault();
                        setDragOverFolderId(f.id);
                      }}
                      onDragLeave={() => setDragOverFolderId(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (!dragItem || dropDisabled) return;
                        if (dragItem.type === "file") {
                          handleMoveFile(dragItem.id, f.id);
                        } else if (dragItem.type === "folder") {
                          handleMoveFolder(dragItem.id, f.id);
                        }
                        setDragItem(null);
                        setDragOverFolderId(null);
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {files.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-zinc-800 rounded-sm text-zinc-500 text-sm">
              <FilesIcon className="w-8 h-8 mx-auto mb-3 opacity-40" />
              {canUpload
                ? currentFolder
                  ? `No files in "${currentFolder.name}" yet. Drop files here or click Upload.`
                  : "No files yet. Drop files here or click Upload."
                : "No files have been uploaded yet."}
            </div>
          ) : (
            <>
              {canUpload && (
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 text-center py-2 border border-dashed border-zinc-800/60 rounded-sm mb-2">
                  Drop files here to upload{currentFolder ? ` into "${currentFolder.name}"` : ""}
                </div>
              )}
              <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="files">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-1"
              >
                {displayedFiles.map((f, idx) => {
                  const Icon = iconFor(f.mime, f.name);
                  const dlUrl = filesApi.downloadUrl(projectId, f.id);
                  const isSelected = selected.has(f.id);
                  const dndDisabled = !canReorder || inSelectionMode;
                  return (
                    <Draggable
                      key={f.id}
                      draggableId={f.id}
                      index={idx}
                      isDragDisabled={dndDisabled}
                    >
                      {(prov, snapshot) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          data-testid={`file-row-${f.id}`}
                          className={`flex items-center gap-2 p-2 rounded-sm border transition-colors ${
                            isSelected
                              ? "bg-amber-500/10 border-amber-500/30"
                              : snapshot.isDragging
                                ? "bg-zinc-950 border-amber-500/40 shadow-lg"
                                : "hover:bg-zinc-950 border-transparent hover:border-zinc-800"
                          }`}
                        >
                          {/* Drag handle (hidden when selecting) */}
                          {canReorder && !inSelectionMode ? (
                            <span
                              {...prov.dragHandleProps}
                              data-testid={`drag-handle-${f.id}`}
                              title="Drag to reorder"
                              className="text-zinc-700 hover:text-zinc-400 cursor-grab active:cursor-grabbing shrink-0"
                            >
                              <GripVertical className="w-3.5 h-3.5" />
                            </span>
                          ) : (
                            <span className="w-3.5 h-3.5 shrink-0" />
                          )}

                          {/* Row checkbox — only when there are bulk actions available */}
                          {canBulkAction ? (
                            <input
                              type="checkbox"
                              data-testid={`select-file-${f.id}`}
                              checked={isSelected}
                              onChange={() => toggleSelect(f.id)}
                              className="w-3.5 h-3.5 accent-amber-500 cursor-pointer shrink-0"
                            />
                          ) : (
                            <span className="w-3.5 h-3.5 shrink-0" />
                          )}

                          <span
                            data-testid={`file-drag-move-${f.id}`}
                            draggable={canManageFolders && !inSelectionMode}
                            onDragStart={(e) => {
                              if (!canManageFolders) return;
                              // Prevent pangea from also picking this up
                              e.stopPropagation();
                              // Firefox needs a non-empty payload to init drag
                              try {
                                e.dataTransfer.setData("text/plain", f.id);
                              } catch { /* noop */ }
                              e.dataTransfer.effectAllowed = "move";
                              setDragItem({ type: "file", id: f.id });
                            }}
                            onDragEnd={() => {
                              setDragItem(null);
                              setDragOverFolderId(null);
                            }}
                            title={canManageFolders ? "Drag into a folder to move" : undefined}
                            className={`shrink-0 ${canManageFolders && !inSelectionMode ? "cursor-grab active:cursor-grabbing" : ""}`}
                          >
                            <Icon className="w-4 h-4 text-zinc-400" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <button
                              type="button"
                              onClick={() => openPreview(f)}
                              data-testid={`file-link-${f.id}`}
                              title="Click to preview"
                              className="text-sm text-zinc-100 hover:text-amber-500 truncate block text-left w-full"
                            >
                              {f.name}
                            </button>
                            <div className="text-[11px] text-zinc-500 font-mono flex items-center gap-2 flex-wrap">
                              <span>
                                {formatBytes(f.size)} · {f.uploaded_by_name || "—"} ·{" "}
                                {new Date(f.uploaded_at).toLocaleDateString()}
                              </span>
                              {f.is_public && (
                                <span
                                  data-testid={`public-badge-${f.id}`}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[10px] uppercase tracking-wider"
                                >
                                  <Globe className="w-2.5 h-2.5" /> Public
                                </span>
                              )}
                            </div>
                          </div>
                          {f.is_public && (
                            <button
                              onClick={() => handleCopyPublicLink(f)}
                              data-testid={`copy-public-${f.id}`}
                              title="Copy public link"
                              className="p-1.5 rounded-sm text-zinc-500 hover:text-emerald-400 hover:bg-zinc-900"
                            >
                              <LinkIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => handleTogglePublic(f)}
                              data-testid={`toggle-public-${f.id}`}
                              title={f.is_public ? "Make private" : "Make public"}
                              className={`p-1.5 rounded-sm hover:bg-zinc-900 ${
                                f.is_public
                                  ? "text-emerald-400 hover:text-emerald-300"
                                  : "text-zinc-500 hover:text-amber-500"
                              }`}
                            >
                              {f.is_public ? (
                                <Globe className="w-3.5 h-3.5" />
                              ) : (
                                <Lock className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}
                          {canDownload && (
                            <a
                              href={dlUrl}
                              download={f.name}
                              data-testid={`download-file-${f.id}`}
                              title="Download"
                              className="p-1.5 rounded-sm text-zinc-500 hover:text-amber-500 hover:bg-zinc-900"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </a>
                          )}
                          {canManageFolders && (folders.length > 0 || currentFolder) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  data-testid={`move-file-${f.id}`}
                                  title="Move to folder"
                                  className="p-1.5 rounded-sm text-zinc-500 hover:text-amber-500 hover:bg-zinc-900"
                                >
                                  <MoveRight className="w-3.5 h-3.5" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="end"
                                className="bg-zinc-950 border-zinc-800 text-zinc-100 min-w-[220px] max-h-[320px] overflow-y-auto"
                              >
                                {currentFolder && (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => handleMoveFile(f.id, null)}
                                      className="text-xs cursor-pointer"
                                      data-testid={`move-file-${f.id}-root`}
                                    >
                                      <ChevronLeft className="w-3.5 h-3.5 mr-2" />
                                      Move to root
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className="bg-zinc-800" />
                                  </>
                                )}
                                {flattenFolders()
                                  .filter(({ folder }) => folder.id !== currentFolderId)
                                  .map(({ folder: fol, depth }) => (
                                    <DropdownMenuItem
                                      key={fol.id}
                                      onClick={() => handleMoveFile(f.id, fol.id)}
                                      className="text-xs cursor-pointer"
                                      data-testid={`move-file-${f.id}-to-${fol.id}`}
                                      style={{ paddingLeft: `${8 + depth * 14}px` }}
                                    >
                                      <Folder className="w-3.5 h-3.5 mr-2 text-amber-400" />
                                      {fol.name}
                                    </DropdownMenuItem>
                                  ))}
                                {flattenFolders().length === 0 && !currentFolder && (
                                  <div className="px-2 py-1.5 text-[11px] text-zinc-500 italic">
                                    No folders yet. Create one to move this file.
                                  </div>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {canDeleteFile(f) && (
                            <button
                              onClick={() => handleRemove(f)}
                              data-testid={`delete-file-${f.id}`}
                              title="Remove"
                              className="p-1.5 rounded-sm text-zinc-500 hover:text-rose-400 hover:bg-zinc-900"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
            </>
          )}
        </div>
      )}

      <FilePreviewModal
        open={!!previewFile}
        onOpenChange={(o) => !o && setPreviewFile(null)}
        projectId={projectId}
        file={previewFile}
        canDownload={canDownload}
      />

      {docViewerFile && (
        <DocumentViewer
          file={docViewerFile}
          projectId={projectId}
          onClose={() => setDocViewerFile(null)}
          previewUrl={filesApi.previewUrl(projectId, docViewerFile.id)}
          downloadUrl={filesApi.downloadUrl(projectId, docViewerFile.id)}
        />
      )}

      {/* New folder dialog */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-light">
              New folder
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Files can be moved between folders anytime. Deleting a folder
              never deletes the files inside — they move back to the root.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateFolder();
            }}
            placeholder="Folder name"
            autoFocus
            data-testid="new-folder-input"
            className="bg-zinc-900 border-zinc-800 text-zinc-100"
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setNewFolderOpen(false)}
              className="text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim()}
              data-testid="new-folder-submit"
              className="bg-amber-500 hover:bg-amber-400 text-black"
            >
              Create folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function FolderTile({
  folder,
  onOpen,
  onStartRename,
  onDelete,
  canManage,
  isRenaming,
  renamingName,
  onRenamingChange,
  onRenameSubmit,
  onRenameCancel,
  // Nested-folder additions (Feb 2026):
  moveOptions = [],
  currentParentId = null,
  onMoveTo,
  isDropTarget = false,
  dropDisabled = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}) {
  if (isRenaming) {
    return (
      <div className="border border-amber-500/40 bg-zinc-900 rounded-sm p-2 flex items-center gap-2">
        <Folder className="w-4 h-4 text-amber-400 shrink-0" />
        <input
          value={renamingName}
          onChange={(e) => onRenamingChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onRenameSubmit();
            if (e.key === "Escape") onRenameCancel();
          }}
          onBlur={onRenameSubmit}
          autoFocus
          data-testid={`folder-rename-input-${folder.id}`}
          className="bg-transparent border-0 outline-none text-sm text-zinc-100 flex-1 min-w-0"
        />
      </div>
    );
  }
  // Highlight when the tile is a valid drop target for the current drag op.
  const tileClass = [
    "group border rounded-sm p-2 flex items-center gap-2 transition-colors cursor-pointer",
    isDropTarget
      ? "border-amber-400 bg-amber-500/10 ring-1 ring-amber-400/50"
      : dropDisabled
        ? "border-zinc-800 bg-zinc-950/40 opacity-50"
        : "border-zinc-800 hover:border-amber-500/40 bg-zinc-950/60 hover:bg-zinc-900/60",
  ].join(" ");
  return (
    <div
      data-testid={`folder-tile-${folder.id}`}
      data-drop-target={isDropTarget ? "true" : "false"}
      className={tileClass}
      onClick={onOpen}
      draggable={canManage}
      onDragStart={(e) => {
        if (!canManage || !onDragStart) return;
        try { e.dataTransfer.setData("text/plain", folder.id); } catch { /* noop */ }
        e.dataTransfer.effectAllowed = "move";
        onDragStart(e);
      }}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <Folder className="w-4 h-4 text-amber-400 shrink-0" />
      <span className="text-sm text-zinc-100 truncate flex-1" title={folder.name}>
        {folder.name}
      </span>
      {canManage && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              data-testid={`folder-menu-${folder.id}`}
              className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-zinc-200 p-0.5"
              aria-label="Folder actions"
            >
              <span className="text-lg leading-none">⋯</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-zinc-950 border-zinc-800 text-zinc-100"
          >
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onStartRename();
              }}
              className="text-xs cursor-pointer"
            >
              <Pencil className="w-3.5 h-3.5 mr-2" /> Rename
            </DropdownMenuItem>
            {onMoveTo && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs cursor-pointer"
                  data-testid={`folder-move-trigger-${folder.id}`}
                >
                  <MoveRight className="w-3.5 h-3.5 mr-2" /> Move to…
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 min-w-[200px] max-h-[300px] overflow-y-auto"
                >
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentParentId !== null) onMoveTo(null);
                    }}
                    disabled={currentParentId === null}
                    className="text-xs cursor-pointer"
                    data-testid={`folder-move-${folder.id}-root`}
                  >
                    <ChevronLeft className="w-3.5 h-3.5 mr-2" />
                    Root
                  </DropdownMenuItem>
                  {moveOptions.length > 0 && (
                    <DropdownMenuSeparator className="bg-zinc-800" />
                  )}
                  {moveOptions.map(({ folder: opt, depth }) => (
                    <DropdownMenuItem
                      key={opt.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (opt.id !== currentParentId) onMoveTo(opt.id);
                      }}
                      disabled={opt.id === currentParentId}
                      className="text-xs cursor-pointer"
                      style={{ paddingLeft: `${8 + depth * 14}px` }}
                      data-testid={`folder-move-${folder.id}-to-${opt.id}`}
                    >
                      <Folder className="w-3.5 h-3.5 mr-2 text-amber-400" />
                      {opt.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="text-xs cursor-pointer text-rose-400 focus:text-rose-300"
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete folder
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

// Small breadcrumb chip used for both root ("All files") and each folder in
// the path. Handles drop-target styling when dragging a file/folder over it.
function BreadcrumbCrumb({
  label,
  isCurrent,
  onClick,
  isDropTarget,
  onDragOver,
  onDragLeave,
  onDrop,
  testid,
}) {
  const base = "px-1.5 py-0.5 rounded-sm flex items-center gap-1 normal-case tracking-normal";
  const state = isCurrent
    ? "text-zinc-200 cursor-default"
    : isDropTarget
      ? "text-amber-300 bg-amber-500/10 ring-1 ring-amber-400/50 cursor-pointer"
      : "text-zinc-400 hover:text-amber-400 cursor-pointer";
  return (
    <button
      type="button"
      onClick={isCurrent ? undefined : onClick}
      data-testid={testid}
      data-drop-target={isDropTarget ? "true" : "false"}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`${base} ${state}`}
    >
      {label}
    </button>
  );
}
